import { Trans, useLingui } from '@lingui/react/macro';
import { useState } from 'react';
import {
  type FeedbackContextPayload,
  FeedbackRateLimitedError,
  type FeedbackTopic,
  submitFeedback,
} from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { Modal, useDrawer } from './drawer.js';
import { Select } from './select.js';
import { magToast } from './toaster.js';

// Feedback modal — opened from the user menu (spec d645e60c, slice 2).
//
// Shape follows create-brand-modal.tsx: portaled backdrop, Escape and
// backdrop-click close, scroll locked behind it.
//
// THE CONTEXT IS THE FEATURE. A founder reporting "this is broken" cannot be
// expected to also say which page they were on, so the client attaches it:
// the href, the brand in the path, their locale, the viewport, the user
// agent. Without those, every bug report starts with us asking where they
// were. Identity is NOT attached — the server reads that from the session,
// because a body field claiming who you are is a body field that can lie.

/** Collected at submit time rather than on mount: a founder can open the
 *  modal, navigate behind it (the drawer routes do this), and then type. The
 *  route we want is the one they were on when they hit send. */
function collectContext(locale: string): FeedbackContextPayload {
  const context: FeedbackContextPayload = {};
  if (typeof window === 'undefined') return context;
  context.href = window.location.href.slice(0, 500);
  // The slug is already inside href; it is lifted out so support can group
  // by brand without parsing URLs.
  const slug = window.location.pathname.match(/^\/brand\/([^/]+)/)?.[1];
  if (slug) context.brandSlug = decodeURIComponent(slug).slice(0, 120);
  context.locale = locale.slice(0, 20);
  context.viewport = `${window.innerWidth}x${window.innerHeight}`;
  context.userAgent = window.navigator.userAgent.slice(0, 400);
  return context;
}

export interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const MIN_MESSAGE = 10;
const MAX_MESSAGE = 4000;

/** The `open` prop is kept so no caller changes. The body is mounted
 *  conditionally so useDrawer's mount effect actually runs — a hook called
 *  inside a component that then returns null would keep `visible` stuck from
 *  the previous open. Spec 748d30e4. */
export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  if (!open) return null;
  return <FeedbackModalBody open onClose={onClose} />;
}

function FeedbackModalBody({ onClose }: FeedbackModalProps) {
  const { t, i18n } = useLingui();
  const [topic, setTopic] = useState<FeedbackTopic>('bug');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const { visible, requestClose } = useDrawer(onClose);

  // Scroll lock, ESC and the portal now come from the Modal shell. The reset
  // effect went with them: the body unmounts on close, so reopening is a
  // fresh mount with fresh state rather than something to clear by hand.
  //
  // SENDING BLOCKS EVERY CLOSE PATH, as it did before — scrim, ESC and the
  // close button all route through here.
  const close = () => {
    if (!sending) requestClose();
  };

  const trimmed = message.trim();
  const canSend = trimmed.length >= MIN_MESSAGE && !sending;

  const onSubmit = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await submitFeedback({
        topic,
        message: trimmed,
        context: collectContext(i18n.locale),
      });
      // Deliberately does not mention whether the mail left. The founder's
      // job ended when they hit send, and "we saved it but the email
      // failed" is our problem to fix, not theirs to worry about.
      magToast.success(t`got it — thank you.`, t`i've passed this to the team.`);
      // requestClose, not onClose — this is the FM-1 path: calling onClose
      // directly here would snap the panel shut and skip the exit entirely.
      requestClose();
    } catch (err) {
      if (err instanceof FeedbackRateLimitedError) {
        magToast.error(
          t`that's a few already.`,
          t`give it an hour and send the next one — the ones you've sent are safe.`,
        );
      } else {
        magToast.error(t`couldn't send that.`, founderErrorCopy(err));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onRequestClose={close}
      maxWidthClass="max-w-[520px]"
      ariaLabelledBy="feedback-modal-title"
    >
      <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[6px_6px_0_var(--plum)] sm:p-6">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>tell us</Trans>
        </p>
        <h2
          id="feedback-modal-title"
          className="mt-1 font-display text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]"
        >
          <Trans>what's on your mind?</Trans>
        </h2>
        <p className="mt-1.5 text-[13px] leading-[1.5] text-[var(--ink)]/65">
          <Trans>
            a bug, something missing, a question — it reaches a human. i'll include the page you're
            on so nobody has to ask.
          </Trans>
        </p>

        <div className="mt-4">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
            <Trans>what kind</Trans>
          </p>
          <div className="mt-1.5">
            <Select<FeedbackTopic>
              value={topic}
              onChange={setTopic}
              ariaLabel={t`what kind of feedback`}
              options={[
                { value: 'bug', label: t`something is broken` },
                { value: 'feature_request', label: t`something is missing` },
                { value: 'question', label: t`a question` },
                { value: 'other', label: t`something else` },
              ]}
            />
          </div>
        </div>

        <div className="mt-4">
          <label
            htmlFor="feedback-message"
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55"
          >
            <Trans>what happened</Trans>
          </label>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
            rows={6}
            disabled={sending}
            placeholder={t`the more specific, the faster we can fix it.`}
            className="mt-1.5 w-full resize-y rounded-xl border border-[var(--ink)]/20 bg-[var(--paper)] p-3 text-[13.5px] leading-[1.5] text-[var(--ink)] outline-none focus:border-[var(--ink)] disabled:opacity-60"
          />
          <p className="mt-1 text-right font-mono text-[10px] text-[var(--ink)]/45">
            {message.length}/{MAX_MESSAGE}
          </p>
        </div>

        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={close}
            disabled={sending}
            className="btn btn-ghost text-[11px] disabled:opacity-60"
          >
            <Trans>cancel</Trans>
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={!canSend}
            className="btn btn-spell btn-sm disabled:opacity-60"
          >
            {sending ? <Trans>sending…</Trans> : <Trans>send</Trans>}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
